import { z } from 'zod';
import { refersTo } from '../common/refers-to';

// The same three tokens as IMM-1383's catalog products and IMM-1386.1's COMPLETENESS_PRODUCT_VALUES,
// so the Datenstand card joins outcome to product by token (.4 asserts the equality once both exist).
export const VALUATION_AGENT_REQUEST_KIND_VALUES = ['ACQUISITION_BRIEFING', 'VALUATION_1A', 'VALUATION_1B'] as const;
export const ValuationAgentRequestKindSchema = z.enum(VALUATION_AGENT_REQUEST_KIND_VALUES);
export type ValuationAgentRequestKind = z.infer<typeof ValuationAgentRequestKindSchema>;

// Effective status: TIMED_OUT requests read FAILED.
export const ValuationAgentRequestStatusSchema = z.enum(['OPEN', 'IN_PROGRESS', 'DELIVERED', 'FAILED']);
export type ValuationAgentRequestStatus = z.infer<typeof ValuationAgentRequestStatusSchema>;

// What the agent may report…
export const VALUATION_AGENT_REPORTABLE_REASON_VALUES = [
  'NO_COMPARISON_DATA',
  'ADDRESS_NOT_FOUND',
  'OBJECT_TYPE_NOT_SUPPORTED',
  'ERROR',
] as const;
// …and what a reader may see: the timeout is derived, never reported.
export const ValuationAgentFailureReasonSchema = z.enum([...VALUATION_AGENT_REPORTABLE_REASON_VALUES, 'TIMED_OUT']);
export type ValuationAgentFailureReason = z.infer<typeof ValuationAgentFailureReasonSchema>;

export const ValuationAgentRequestSchema = z.object({
  id: z.uuid(),
  kind: ValuationAgentRequestKindSchema,
  status: ValuationAgentRequestStatusSchema,
  workspaceId: z.uuid(),
  propertyId: z.uuid(),
  dealId: z.uuid().nullable(),
  requestedByMemberId: z.uuid(),
  requestedAt: z.iso.datetime(),
  takenAt: z.iso.datetime().nullable(),
  // When it times out unless answered: (takenAt ?? requestedAt) + 30 min. Null once DELIVERED or FAILED.
  deadline: z.iso.datetime().nullable(),
  reason: ValuationAgentFailureReasonSchema.nullable(), // non-null iff FAILED
  hint: z.string().nullable(), // non-null iff reason === 'ERROR'
});
export type ValuationAgentRequestDto = z.infer<typeof ValuationAgentRequestSchema>;

export const ValuationAgentInboxResponseSchema = z.object({ items: z.array(ValuationAgentRequestSchema) });
export type ValuationAgentInboxResponse = z.infer<typeof ValuationAgentInboxResponseSchema>;

export const ReportValuationAgentReasonBodySchema = z.discriminatedUnion('reason', [
  z.object({ reason: z.literal('ERROR'), hint: z.string().trim().min(1).max(300) }).strict(),
  z.object({ reason: z.enum(['NO_COMPARISON_DATA', 'ADDRESS_NOT_FOUND', 'OBJECT_TYPE_NOT_SUPPORTED']) }).strict(),
]);
export type ReportValuationAgentReasonBody = z.infer<typeof ReportValuationAgentReasonBodySchema>;

export const ValuationAgentRequestPathParamsSchema = z.object({ id: z.uuid() });
export const TakeValuationAgentRequestBodySchema = z.object({}).strict();

export const PropertyValuationAgentRequestsPathParamsSchema = z.object({ organizationId: z.uuid(), id: z.uuid() });

export const PropertyValuationAgentOutcomeSchema = z.object({
  kind: ValuationAgentRequestKindSchema,
  status: ValuationAgentRequestStatusSchema, // effective
  reason: ValuationAgentFailureReasonSchema.nullable(), // non-null iff FAILED; TIMED_OUT when derived
  hint: z.string().nullable(), // non-null iff reason === 'ERROR'
  requestedAt: z.iso.datetime(),
  dealId: refersTo('deal', z.uuid()).nullable(),
});
export type PropertyValuationAgentOutcome = z.infer<typeof PropertyValuationAgentOutcomeSchema>;

export const PropertyValuationAgentRequestsResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  // At most one per kind (the latest by requestedAt), in VALUATION_AGENT_REQUEST_KIND_VALUES order. A kind with no request is absent.
  items: z.array(PropertyValuationAgentOutcomeSchema),
});
export type PropertyValuationAgentRequestsResponse = z.infer<typeof PropertyValuationAgentRequestsResponseSchema>;
